/**
 * The suite's single `localStorage` accessor
 *
 * Every key this module writes is `regard:<namespace>:<key>`.
 *
 * Nothing outside this directory may touch `localStorage` directly
 * (With the one exception being the Auth0 SDK's writes).
 *
 * Values are strings, as the Web Storage API stores them. A caller wanting structured state
 * serializes at its own call site.
 */

/** Shared by every key, so one prefix distinguishes what this module owns from what it does not. */
const KEY_PREFIX = 'regard'

const SEPARATOR = ':'

/** Reads, writes, and removals within one namespace. Obtained from {@link storage.for}. */
interface NamespacedStorage {
  /** The stored value, or null when it is unset or storage is unavailable. */
  get: (key: string) => string | null
  /** Stores the value, or does nothing when storage is unavailable or the write is rejected. */
  set: (key: string, value: string) => void
  /** Removes this namespace's key alone, leaving every other namespace's untouched. */
  remove: (key: string) => void
  /** Removes every value in this namespace. */
  clear: () => void
}

/**
 * Reads through a guard, yielding `fallback` where storage is unavailable.
 *
 * Storage fails two ways and both degrade rather than propagate, since losing scratch state should
 * not crash the app.
 * 1. Private mode and disabled storage throw on the `window.localStorage` property access itself,
 *    so a guard has to wrap the access and not merely the method call.
 * 2. An exhausted quota throws from `setItem` alone, per write rather than per session, which is why
 *    there is no cached availability probe.
 * Neither failure is distinguished, because the contract is the same for both.
 */
const readOrFallBackTo = <Result>(
  read: () => Result,
  fallback: Result,
): Result => {
  try {
    return read()
  } catch {
    return fallback
  }
}

/**
 * Runs a write through a guard, doing nothing where storage is unavailable or rejects the write.
 *
 * The write counterpart of {@link readOrFallBackTo}, which carries the reasoning for both.
 */
const writeOrSkip = (write: () => void): void => {
  try {
    write()
  } catch {
    // Deliberately silent, for the reasons on `readOrFallBackTo`.
  }
}

/**
 * Builds a handle over one namespace.
 *
 * The namespace is validated rather than trusted because a colon in it would make the grammar
 * ambiguous: `for('shell:last').get('Customer')` and `for('shell').get('last:Customer')` would name
 * one physical key from two call sites, which is the collision the module exists to prevent. A
 * colon *within* a key is fine and deliberately allowed: it can only collide inside the namespace
 * that wrote it, so it is that caller's own business.
 */
const namespacedStorage = (namespace: string): NamespacedStorage => {
  if (namespace === '') {
    throw new Error('A storage namespace cannot be empty.')
  }
  if (namespace.includes(SEPARATOR)) {
    throw new Error(
      `A storage namespace cannot contain "${SEPARATOR}", which would make the key ambiguous (got "${namespace}").`,
    )
  }

  const physicalKey = (key: string): string =>
    [KEY_PREFIX, namespace, key].join(SEPARATOR)

  return {
    get: (key) =>
      readOrFallBackTo(
        () => window.localStorage.getItem(physicalKey(key)),
        null,
      ),
    set: (key, value) =>
      writeOrSkip(() => {
        window.localStorage.setItem(physicalKey(key), value)
      }),
    remove: (key) =>
      writeOrSkip(() => {
        window.localStorage.removeItem(physicalKey(key))
      }),
    clear: () =>
      writeOrSkip(() => {
        for (const key of Object.keys(window.localStorage)) {
          if (key.startsWith(physicalKey(''))) {
            window.localStorage.removeItem(key)
          }
        }
      }),
  }
}

export const storage = {
  /**
   * A handle over one namespace, which is how every caller reaches storage.
   *
   * Hold the result as a module constant rather than calling this per access: it validates its
   * argument, and building a handle is the one operation here that can throw. It does not touch
   * storage, so a handle is safe to build where storage is unavailable.
   *
   * An app's namespace is its own id. `shell` is the shell's, holding the session-adjacent state an
   * app has no business reading, so an app asking for it is a layering violation a review should
   * catch rather than something the module refuses.
   */
  for: namespacedStorage,

  /**
   * Removes every key this module owns, across all namespaces.
   *
   * For test teardown, which is why it exists: it replaces the `localStorage.clear()` calls that
   * would otherwise be the one reason for tests to reach the bare API. Scoped to the `regard:`
   * prefix, so it is exactly as wide as the module's own writes and leaves the Auth0 session cache
   * standing where `clear()` would have evicted it.
   */
  clearAll: (): void =>
    writeOrSkip(() => {
      const ownedKeys = Object.keys(window.localStorage).filter((key) =>
        key.startsWith(`${KEY_PREFIX}${SEPARATOR}`),
      )
      ownedKeys.forEach((key) => {
        window.localStorage.removeItem(key)
      })
    }),
}
